import { useEffect, useRef, useCallback } from "react";
import type { AuditRecord, EliminationLogEntry } from "../types/audit.types";
import type {
  FullPathwaySnapshot,
  PathwayEngineSnapshot,
  AhtChainSnapshot,
  EngineState,
} from "./types";
import { serializeToTemplate, serializeToSections } from "./serialize";
import {
  addConsultationTemplate,
  updateConsultationTemplate,
} from "@/dispatchers/patientDispatchers";
import { getAuthHeader } from "@/services/authToken";
import config from "@/config";

const DEBOUNCE_MS = 1000;

interface AutoSaveArgs {
  consultationId: string;
  companyId: string;
  templateName: string;
  hasExistingTemplate: boolean;
  templateId?: string;

  pathwayId: string;
  engineState: EngineState;
  currentNodeId: string;
  history: string[];
  answers: Map<string, unknown>;
  contextValues: Record<string, unknown>;
  audit: AuditRecord;
  selectedPgdId: string | null;
  exclusionAnswers: Record<string, "yes" | "no">;
  capturedWeightKg: number | null;

  eligibleDrugIds: string[];
  eliminationLog: EliminationLogEntry[];
  resolvedDrugId: string | null;
  accumulatedFlags: string[];
  managementPlanCaptures: {
    nodeId: string;
    planText: string;
    patientAcknowledged: boolean;
    capturedAt: string;
  }[];
  preferenceCaptures: {
    nodeId: string;
    key: string;
    value: unknown;
    capturedAt: string;
  }[];

  chainState?: AhtChainSnapshot;
}

function buildSnapshot(args: AutoSaveArgs): FullPathwaySnapshot {
  const engine: PathwayEngineSnapshot = {
    version: 1,
    pathwayId: args.pathwayId,
    savedAt: new Date().toISOString(),
    engineState: args.engineState,
    currentNodeId: args.currentNodeId,
    history: args.history,
    answers: Object.fromEntries(args.answers),
    contextValues: args.contextValues,
    audit: args.audit,
    selectedPgdId: args.selectedPgdId,
    exclusionAnswers: args.exclusionAnswers,
    capturedWeightKg: args.capturedWeightKg,
    eligibleDrugIds: args.eligibleDrugIds,
    eliminationLog: args.eliminationLog,
    resolvedDrugId: args.resolvedDrugId,
    accumulatedFlags: args.accumulatedFlags,
    managementPlanCaptures: args.managementPlanCaptures,
    preferenceCaptures: args.preferenceCaptures,
  };

  return { engine, chain: args.chainState };
}

export function usePathwayAutoSave(args: AutoSaveArgs) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSavedOnce = useRef(false);
  const latestArgsRef = useRef(args);
  latestArgsRef.current = args;

  const doSave = useCallback(async () => {
    const a = latestArgsRef.current;
    if (!a.consultationId) return;
    if (a.engineState === "complete" || a.engineState === "banner") return;

    const snapshot = buildSnapshot(a);

    try {
      if (!hasSavedOnce.current && !a.hasExistingTemplate) {
        const template = serializeToTemplate(snapshot, a.templateName, a.companyId);
        if (a.templateId) {
          (template as any)._id = a.templateId;
        }
        await addConsultationTemplate({
          consultationId: a.consultationId,
          template,
        });
      } else {
        const sections = serializeToSections(snapshot);
        await updateConsultationTemplate({
          consultationId: a.consultationId,
          payload: sections,
        });
      }
      hasSavedOnce.current = true;
    } catch (err) {
      console.error("[PathwayAutoSave] save failed:", err);
    }
  }, []);

  useEffect(() => {
    if (args.engineState === "complete" || args.engineState === "banner") return;
    if (!args.consultationId) return;
    if (args.answers.size === 0 && args.history.length === 0) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      doSave();
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [args.currentNodeId, args.answers.size, args.engineState, doSave]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const a = latestArgsRef.current;
      if (!a.consultationId) return;
      if (a.engineState === "complete" || a.engineState === "banner") return;
      if (a.answers.size === 0 && a.history.length === 0) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const snapshot = buildSnapshot(a);
      const sections = serializeToSections(snapshot);
      const baseUrl = config.localurl || "";
      const url = `${baseUrl}/PatientConsultation/update-consultation-template?consultationID=${a.consultationId}`;
      const authHeader = getAuthHeader();

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (authHeader) headers["Authorization"] = authHeader;

        fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(sections),
          keepalive: true,
        });
      } catch {
        // fetch with keepalive failed — max ~1 second data loss
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
}
